import { Link, NavLink, useNavigate } from "react-router-dom";
import {
    Sparkles,
    BookOpen,
    Wand2,
    FlaskConical,
    Droplet,
    Sprout,
    LayoutDashboard,
    LogOut,
    LogIn,
    Languages,
    Zap,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import NotificationsBell from "@/components/NotificationsBell";

const linkBase =
    "font-mono uppercase text-xs tracking-[0.18em] px-2 md:px-3 py-2 transition-colors";

export default function Nav() {
    const { user, logout } = useAuth();
    const { lang, toggle, t } = useLang();
    const nav = useNavigate();

    const doLogout = async () => {
        await logout();
        nav("/", { replace: true });
    };

    return (
        <header className="nav sticky top-0 z-40 no-print" data-testid="site-nav">
            <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 gap-2">
                <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
                    <span className="font-display font-black text-white text-lg tracking-tight">
                        FLOW
                    </span>
                    <span className="font-brush text-[var(--lime)] text-2xl -mt-1 -ml-1 group-hover:text-[var(--cyan)] transition-colors">
                        .lab
                    </span>
                </Link>
                <nav className="flex items-center gap-0 md:gap-1">
                    <NavLink
                        to="/curriculum"
                        data-testid="nav-curriculum"
                        className={({ isActive }) =>
                            `${linkBase} ${isActive ? "text-[var(--lime)]" : "text-[var(--muted)] hover:text-white"}`
                        }
                    >
                        <span className="hidden md:inline">{t.nav.curriculum}</span>
                        <BookOpen size={16} className="md:hidden inline" />
                    </NavLink>
                    <NavLink
                        to="/grow-lab"
                        data-testid="nav-grow-lab"
                        className={({ isActive }) =>
                            `${linkBase} ${isActive ? "text-[var(--lime)]" : "text-[var(--muted)] hover:text-white"}`
                        }
                    >
                        <span className="hidden md:inline">{t.nav.growLab}</span>
                        <FlaskConical size={16} className="md:hidden inline" />
                    </NavLink>
                    <NavLink
                        to="/water-lab"
                        data-testid="nav-water-lab"
                        className={({ isActive }) =>
                            `${linkBase} ${isActive ? "text-[var(--lime)]" : "text-[var(--muted)] hover:text-white"}`
                        }
                    >
                        <span className="hidden lg:inline">{t.nav.waterLab}</span>
                        <Droplet size={16} className="lg:hidden inline" />
                    </NavLink>
                    <NavLink
                        to="/plant-picker"
                        data-testid="nav-plant-picker"
                        className={({ isActive }) =>
                            `${linkBase} ${isActive ? "text-[var(--lime)]" : "text-[var(--muted)] hover:text-white"}`
                        }
                    >
                        <span className="hidden lg:inline">{t.nav.plantPicker}</span>
                        <Sprout size={16} className="lg:hidden inline" />
                    </NavLink>
                    <NavLink
                        to="/teach"
                        data-testid="nav-teach"
                        className={({ isActive }) =>
                            `${linkBase} ${isActive ? "text-[var(--lime)]" : "text-[var(--muted)] hover:text-white"}`
                        }
                    >
                        <span className="hidden md:inline">{t.nav.teach}</span>
                        <Wand2 size={16} className="md:hidden inline" />
                    </NavLink>
                    {user && (
                        <NavLink
                            to="/dashboard"
                            data-testid="nav-dashboard"
                            className={({ isActive }) =>
                                `${linkBase} ${isActive ? "text-[var(--lime)]" : "text-[var(--muted)] hover:text-white"}`
                            }
                        >
                            <span className="hidden md:inline">{t.nav.dashboard}</span>
                            <LayoutDashboard size={16} className="md:hidden inline" />
                        </NavLink>
                    )}
                    <NavLink
                        to="/about"
                        data-testid="nav-about"
                        className={({ isActive }) =>
                            `${linkBase} ${isActive ? "text-[var(--lime)]" : "text-[var(--muted)] hover:text-white"}`
                        }
                    >
                        <span className="hidden md:inline">{t.nav.about}</span>
                        <Sparkles size={16} className="md:hidden inline" />
                    </NavLink>

                    <button
                        onClick={toggle}
                        className={`${linkBase} text-[var(--cyan)] hover:text-[var(--lime)] flex items-center gap-1`}
                        data-testid="lang-toggle"
                        aria-label="Toggle language"
                    >
                        <Languages size={14} />
                        <span className="text-[10px]">{lang.toUpperCase()}</span>
                    </button>

                    <NotificationsBell />

                    {/* Pricing / Upgrade pill */}
                    {user?.tier && user.tier !== "free" ? (
                        <Link
                            to="/pricing"
                            className="font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 border border-[var(--lime)] text-[var(--lime)] hover:bg-[var(--lime)] hover:text-black"
                            data-testid="nav-pro-pill"
                            title={user.tier === "trial" ? "Trial active" : "Pro active"}
                        >
                            {user.tier === "trial" ? "TRIAL" : "PRO"}
                        </Link>
                    ) : (
                        <Link
                            to="/pricing"
                            className="font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 border border-[var(--border)] text-[var(--muted)] hover:text-[var(--lime)] hover:border-[var(--lime)] inline-flex items-center gap-1"
                            data-testid="nav-upgrade"
                        >
                            <Zap size={11} />
                            <span className="hidden md:inline">
                                {lang === "es" ? "Pro" : "Upgrade"}
                            </span>
                        </Link>
                    )}

                    {user ? (
                        <button
                            onClick={doLogout}
                            className={`${linkBase} text-[var(--muted)] hover:text-[var(--coral)]`}
                            data-testid="nav-logout"
                        >
                            <LogOut size={14} className="inline mr-1" />
                            <span className="hidden md:inline">{t.nav.logout}</span>
                        </button>
                    ) : (
                        <NavLink
                            to="/login"
                            className={({ isActive }) =>
                                `${linkBase} ${isActive ? "text-[var(--lime)]" : "text-[var(--cyan)] hover:text-[var(--lime)]"}`
                            }
                            data-testid="nav-login"
                        >
                            <LogIn size={14} className="inline mr-1" />
                            <span className="hidden md:inline">{t.nav.login}</span>
                        </NavLink>
                    )}
                </nav>
            </div>
        </header>
    );
}
